import { get } from 'lodash';

import { getLocalStorageApi, STORAGE_KEYS } from 'utilities/localStorage';
import { GLOBAL_SET_USER_ENVIRONMENT } from 'reducers/reducer';
import { parseClientError } from 'helpers/errorHelpers';

export const ROLES = {
  ADMIN: 'admin',
  AUTHOR: 'author',
  FULL_CONTROL: 'full control',
  VIEW_ONLY: 'view only'
};

export const FINANCIAL_PERMISSIONS = {
  EDIT: 'edit',
  NONE: 'none',
  VIEW: 'view'
};

const ROLE_PRECEDENCE = {
  admin: 1,
  fullControl: 2,
  author: 3,
  viewOnly: 4
};

export const defaultOrgStorageApi = getLocalStorageApi(
  STORAGE_KEYS.DEFAULT_ORG,
  null
);

const oktaTokenStorageApi = getLocalStorageApi('okta-token-storage');

export const getOrganization = (organizations, id) => {
  const result = organizations.find(organization => organization._id === id);
  return result ? { id: result._id, name: result.name } : null;
};

const getLastAccessedOrg = (organizations, selectedOrgId) => {
  if (!selectedOrgId) {
    return null;
  }
  const org = getOrganization(organizations, selectedOrgId);
  if (org) {
    defaultOrgStorageApi.save(org);
  }
  return org;
};

// create a hash table from the permissions array based on the org id to get highest permission for each org
const createPermissionsHash = permissions =>
  permissions
    .sort((a, b) => {
      const aRole = ROLE_PRECEDENCE[a.role] || 999;
      const bRole = ROLE_PRECEDENCE[b.role] || 999;
      // sort lowest to highest precedence
      return bRole - aRole;
    })
    .reduce(
      (accum, perm) => ({
        ...accum,
        [perm.organizationId]: perm
      }),
      {}
    );

// returns an array of (organization,permission) tuples using the permissions hash table
const getPermittedOrganizationTuples = (permissionHash, organizations) =>
  Object.entries(permissionHash)
    .map(([orgId, perm]) => {
      const organization = organizations.find(org => org._id === orgId);
      return [organization, perm];
    })
    // we sometimes have permissions data referring to orgs that no longer exist
    .filter(([org]) => !!org);

// sort (organization,permission) tuples by org name
const sortOrganizationPermissionsByName = tuples =>
  tuples.sort(([a], [b]) =>
    (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase())
  );

// get array of (organization,permission) tuples sorted by org name
const createOrganizationPermissions = (organizations, permissions) => {
  // hash the perms to get the highest permission for each org
  const permissionsHash = createPermissionsHash(permissions);
  const permittedOrganizationTuples = getPermittedOrganizationTuples(
    permissionsHash,
    organizations
  );
  return sortOrganizationPermissionsByName(permittedOrganizationTuples);
};

/**
 * get list of organizations from array of (organization,permission) tuples
 * @param organizationPermissionsTuples {Array} - the array of (organization,permission) tuples from global state
 * @returns {Array} - the organizations array
 */
export const extractOrgsFromOrganizationPermissions = organizationPermissionsTuples =>
  organizationPermissionsTuples.map(([org]) => org);

/**
 * get list of permissions from array of (organization,permission) tuples
 * @param organizationPermissionsTuples {Array} - the array of (organization,permission) tuples from global state
 * @returns {Array} - the permissions array
 */
export const extractPermsFromOrganizationPermissions = organizationPermissionsTuples =>
  organizationPermissionsTuples.map(([, perm]) => perm);

/**
 * create a new organizationPermissions tuples that includes the specified new organization and permission
 * @param organizationPermissions {Array} - the current array of (organization,permission) tuples from global state
 * @param newOrganization {Object} - the new organization to be added
 * @param newPermission {Object} - the new permission to be added
 * @returns {Array} - an updated version of organizationPermissions
 */
export const addOrganizationPermissions = (
  organizationPermissions,
  newOrganization,
  newPermission
) => {
  const orgs = extractOrgsFromOrganizationPermissions(organizationPermissions);
  const perms = extractPermsFromOrganizationPermissions(
    organizationPermissions
  );
  return createOrganizationPermissions(
    [...orgs, newOrganization],
    [...perms, newPermission]
  );
};

// filter permissions tuples by selected org, then pick the first one
const getSelectedPermission = (organizationPermissions, selectedOrg) => {
  const [firstTuple] = organizationPermissions.filter(
    ([org]) => !selectedOrg || org._id === selectedOrg.id
  );
  const [, permission] = firstTuple || [];
  return permission;
};

/**
 * determine which organization and permission to use based on local storage, last accessed data, and permitted orgs
 * @param organizations {Array} - all organizations
 * @param members {Array} - all org members
 * @param permissions {Array} - all user permissions
 * @param selectedOrganization {Object} - optional, specified org to use as the selected org
 * @returns {{currentOrganization: ({name: String, id: String}), currentPermission: *, currentOrganizationPermissions: Array }}
 */
export const getCurrentOrganizationAndPermissions = (
  organizations,
  members,
  permissions,
  selectedOrganization
) => {
  const defaultOrg = defaultOrgStorageApi.get();
  const tokenStore = oktaTokenStorageApi.get();

  // When a user logs out, they will have a localStorage okta-token-storage
  // object, but it will be an empty object
  if (tokenStore && Object.keys(tokenStore).length !== 0) {
    const oktaClientId = tokenStore.idToken.claims.sub;
    const currentOrganizationPermissions = createOrganizationPermissions(
      organizations,
      permissions
    );
    const user = members.find(member => member.oktaId === oktaClientId);
    // check user last accessed org
    const lastAccessedOrg = getLastAccessedOrg(
      organizations,
      user && user.organizationId
    );
    const selOrg = selectedOrganization
      ? getOrganization(organizations, selectedOrganization._id)
      : lastAccessedOrg || defaultOrg;
    const permission = getSelectedPermission(
      currentOrganizationPermissions,
      selOrg
    );
    const pickOrgId =
      (selectedOrganization && selectedOrganization._id) ||
      (permission && permission.organizationId) ||
      (lastAccessedOrg && lastAccessedOrg.id) ||
      (defaultOrg && defaultOrg.id);
    const currentOrganization =
      getOrganization(organizations, pickOrgId) || defaultOrg || {};
    const currentPermission = getSelectedPermission(
      currentOrganizationPermissions,
      currentOrganization
    );
    if (currentOrganization.id) {
      defaultOrgStorageApi.save(currentOrganization);
    } else if (organizations.length > 0) {
      defaultOrgStorageApi.remove();
    }
    return {
      currentOrganization,
      currentPermission,
      currentOrganizationPermissions
    };
  }
  // only unit tests or unauthenticated users fall thru to this point
  return {
    currentOrganization: {
      id: "9a1a1673-6926-41fa-9d71-a5aad6f67cb6",
      name: 'Test Org'
    },
    currentOrganizationPermissions: []
  };
};

// check for unauthorized errors in api responses
const parseError = error => {
  const message =
    get(error, 'response.data.message') || get(error, 'response.data') || '';
  if (message.includes('AuthenticationError')) {
    return { unauthorized: true };
  }
  // do not show the error toast, just fall thru to the redirect to welcome
  if (message.includes('PermissionService encountered an error')) {
    return {};
  }
  return { error };
};

// show a toast message indicating the user does not have permissions
export const toastNoPermissions = dispatch =>
  parseClientError(dispatch)(
    'You do not have permissions for the selected organization.'
  );

/**
 * when the selected org is being changed, this function updates local storage, loads permissions, and updates global state
 * @param dispatch - the global state dispatch function
 * @param selectedOrg {Object} - the organization being set as the selected org
 * @param organizationPermissions {Array} - the array of all (organizations, permission) tuples from global state
 * @returns {Promise<{currentOrganization: ({name: String, id: String}), currentPermission: *}|{error: *}>}
 */
export const selectOrganization = async (
  dispatch,
  selectedOrg,
  organizationPermissions
) => {
  try {
    const permissions = extractPermsFromOrganizationPermissions(
      organizationPermissions
    );
    const organizations = extractOrgsFromOrganizationPermissions(
      organizationPermissions
    );

    const {
      currentOrganization,
      currentPermission
    } = getCurrentOrganizationAndPermissions(
      organizations,
      [],
      permissions,
      selectedOrg
    );
    dispatch({
      type: GLOBAL_SET_USER_ENVIRONMENT,
      organization: currentOrganization,
      permission: currentPermission,
      organizationPermissions
    });
    return { currentOrganization, currentPermission };
  } catch (err) {
    const { error, unauthorized } = parseError(err);
    if (unauthorized || !error) {
      toastNoPermissions(dispatch);
      dispatch({
        type: GLOBAL_SET_USER_ENVIRONMENT,
        organizationPermissions
      });
    }
    return { error, unauthorized };
  }
};
